<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <div aria-live="polite" role="status">
      <AddressConfirmation
        :address-list
        :items
        :last-collection-point
        :loading
        :swap-offer
        @update="handleUpdate"
      />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, onMounted } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { hasSelectedMobilePlan } from '@backmarket/nuxt-layer-checkout/utils/mobilePlan/hasSelectedMobilePlan'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'

import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useRedirectIfAddressNeedsBouyguesNormalization } from '~/scopes/checkout/composables/redirects/useRedirectIfAddressNeedsBouyguesNormalization'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useRedirectIfMissingShippingInformation } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingShippingInformation'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'

import Stepper from '../../components/Stepper/Stepper.vue'

import AddressConfirmation from './components/AddressConfirmation/AddressConfirmation.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', callback?: () => void): void
}>()

const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('ADDRESS_CONFIRMATION')
const { redirectIfAddressNeedsBouyguesNormalization } =
  useRedirectIfAddressNeedsBouyguesNormalization()
const { redirectIfMissingShippingInformation } =
  useRedirectIfMissingShippingInformation('ADDRESS_CONFIRMATION')
const experiments = useExperiments()

const isSinglePageAddressesEnabled = computed(() => {
  return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
})

onMounted(async () => {
  if (isSinglePageAddressesEnabled.value) {
    await navigateTo({ name: CHECKOUT.ADDRESSES })

    return
  }

  await redirectIfCartEmpty(props.items, CHECKOUT.CART)
  await redirectIfMissingShippingInformation(
    props.addressList,
    props.shippableCountries,
  )
  if (hasSelectedMobilePlan(props.items)) {
    await redirectIfAddressNeedsBouyguesNormalization(props.addressList)
  }

  trackFunnel(props.items, props.swapOffer)
})

async function handleUpdate(callback?: () => void) {
  emit('update', callback)
}
</script>
